<template>
  <div class="about-page">
    <!-- 英雄区域 -->
    <div class="hero-section">
      <div class="container mx-auto px-4 py-16 lg:py-24">
        <div class="text-center">
          <h1 class="text-5xl lg:text-7xl font-orbitron font-bold mb-6 leading-tight">
            About <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-cyber-purple">CyberGame Hub</span>
          </h1>
          <p class="text-xl lg:text-2xl text-cyber-light/70 mb-8 max-w-4xl mx-auto leading-relaxed">
            The ultimate destination for instant gaming experiences. We're revolutionizing how people play, 
            connect, and discover games in the digital age.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <div class="cyber-button-large">
              <span class="text-2xl font-orbitron">2.5M+</span>
              <span class="text-sm">Active Players</span>
            </div>
            <div class="cyber-button-large">
              <span class="text-2xl font-orbitron">500+</span>
              <span class="text-sm">Games Available</span>
            </div>
            <div class="cyber-button-large">
              <span class="text-2xl font-orbitron">150+</span>
              <span class="text-sm">Countries</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 我们的故事 -->
    <section class="story-section">
      <div class="container mx-auto px-4 py-16">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-4xl font-orbitron font-bold mb-6 text-cyber-light">Our Story</h2>
            <p class="text-cyber-light/70 text-lg mb-6 leading-relaxed">
              Founded in 2023, CyberGame Hub emerged from a simple yet powerful vision: to democratize gaming by eliminating traditional barriers. We recognized that expensive hardware requirements, lengthy download times, and complex installations were preventing millions of potential gamers from enjoying quality gaming experiences.
            </p>
            <p class="text-cyber-light/70 text-lg mb-6 leading-relaxed">
              Our diverse team of passionate developers, UX designers, gaming industry veterans, and community managers collaborated to create a revolutionary platform. Using advanced iframe technology and cloud-based gaming infrastructure, we developed a system where players can instantly access over 500 carefully curated games directly in their browser, regardless of device specifications.
            </p>
            <p class="text-cyber-light/70 text-lg mb-6 leading-relaxed">
              What sets us apart is our commitment to quality and community. Every game on our platform undergoes rigorous testing for performance, security, and user experience. We've built partnerships with independent game developers worldwide, providing them a platform to showcase their creativity while ensuring players discover unique, high-quality gaming experiences.
            </p>
            <p class="text-cyber-light/70 text-lg leading-relaxed">
              Today, we're proud to serve over 2.5 million active players across 150+ countries, facilitating millions of gaming sessions monthly. Our platform supports 15+ game genres, from classic arcade games to modern indie masterpieces, all optimized for seamless browser-based play.
            </p>
          </div>
          <div class="relative">
            <div class="cyber-card-large p-8">
              <div class="grid grid-cols-2 gap-4">
                <div class="text-center">
                  <div class="text-3xl font-orbitron font-bold text-cyber-blue mb-2">2023</div>
                  <div class="text-cyber-light/70 text-sm">Founded</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-orbitron font-bold text-cyber-purple mb-2">1M+</div>
                  <div class="text-cyber-light/70 text-sm">First Year Users</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-orbitron font-bold text-cyber-green mb-2">100+</div>
                  <div class="text-cyber-light/70 text-sm">Game Partners</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-orbitron font-bold text-cyber-blue mb-2">24/7</div>
                  <div class="text-cyber-light/70 text-sm">Support</div>
                </div>
              </div>
            </div>
            <div class="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-full opacity-20"></div>
            <div class="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-cyber-purple to-cyber-green rounded-full opacity-20"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 我们的使命 -->
    <section class="mission-section">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-orbitron font-bold mb-6 text-cyber-light">Our Mission</h2>
          <p class="text-xl text-cyber-light/70 max-w-3xl mx-auto">
            To democratize gaming by providing instant access to high-quality games for everyone, 
            regardless of their device or location.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="cyber-card text-center p-8">
            <div class="w-20 h-20 bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg class="w-10 h-10 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-orbitron font-bold mb-4 text-cyber-light">Instant Access</h3>
            <p class="text-cyber-light/70 leading-relaxed">
              No downloads, no installations, no waiting. Jump into any game instantly with our 
              revolutionary iframe technology.
            </p>
          </div>
          
          <div class="cyber-card text-center p-8">
            <div class="w-20 h-20 bg-gradient-to-r from-cyber-purple to-cyber-green rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg class="w-10 h-10 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-orbitron font-bold mb-4 text-cyber-light">Global Community</h3>
            <p class="text-cyber-light/70 leading-relaxed">
              Connect with players from around the world. Share experiences, compete, and build 
              lasting friendships in our vibrant gaming community.
            </p>
          </div>
          
          <div class="cyber-card text-center p-8">
            <div class="w-20 h-20 bg-gradient-to-r from-cyber-green to-cyber-blue rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg class="w-10 h-10 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-orbitron font-bold mb-4 text-cyber-light">Safe & Secure</h3>
            <p class="text-cyber-light/70 leading-relaxed">
              Your safety is our priority. We implement enterprise-grade security measures to 
              protect your data and ensure a safe gaming environment.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 技术优势 -->
    <section class="tech-section">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-orbitron font-bold mb-6 text-cyber-light">Technology</h2>
          <p class="text-xl text-cyber-light/70 max-w-3xl mx-auto">
            Powered by cutting-edge technology that delivers seamless gaming experiences across all devices.
          </p>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div class="cyber-card p-8">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Advanced iframe Technology</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">Instant Loading</h4>
                  <p class="text-cyber-light/70 text-sm">Games load in milliseconds with our optimized iframe system</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-purple rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">Cross-Platform</h4>
                  <p class="text-cyber-light/70 text-sm">Works seamlessly on desktop, tablet, and mobile devices</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">No Downloads</h4>
                  <p class="text-cyber-light/70 text-sm">Save storage space and time with cloud-based gaming</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="cyber-card p-8">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Performance & Security</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">Global CDN</h4>
                  <p class="text-cyber-light/70 text-sm">Lightning-fast loading from servers worldwide</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-purple rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">SSL Encryption</h4>
                  <p class="text-cyber-light/70 text-sm">Bank-level security for all data transmission</p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div class="w-8 h-8 bg-cyber-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg class="w-4 h-4 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-cyber-light font-semibold mb-1">24/7 Monitoring</h4>
                  <p class="text-cyber-light/70 text-sm">Continuous system monitoring and optimization</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 团队介绍 -->
    <section class="team-section">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-orbitron font-bold mb-6 text-cyber-light">Meet Our Team</h2>
          <p class="text-xl text-cyber-light/70 max-w-3xl mx-auto">
            A diverse group of passionate individuals dedicated to revolutionizing the gaming experience.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="cyber-card text-center p-6">
            <div class="w-24 h-24 bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-full mx-auto mb-4 flex items-center justify-center">
              <span class="text-cyber-dark font-orbitron font-bold text-xl">JD</span>
            </div>
            <h3 class="text-lg font-orbitron font-bold mb-2 text-cyber-light">John Doe</h3>
            <p class="text-cyber-blue text-sm mb-3">CEO & Founder</p>
            <p class="text-cyber-light/70 text-xs leading-relaxed">
              Gaming enthusiast with 15+ years of experience in the industry. Former lead developer at major gaming studios.
            </p>
          </div>
          
          <div class="cyber-card text-center p-6">
            <div class="w-24 h-24 bg-gradient-to-r from-cyber-purple to-cyber-green rounded-full mx-auto mb-4 flex items-center justify-center">
              <span class="text-cyber-dark font-orbitron font-bold text-xl">JS</span>
            </div>
            <h3 class="text-lg font-orbitron font-bold mb-2 text-cyber-light">Jane Smith</h3>
            <p class="text-cyber-purple text-sm mb-3">CTO</p>
            <p class="text-cyber-light/70 text-xs leading-relaxed">
              Technology expert specializing in cloud gaming and AI. PhD in Computer Science from MIT.
            </p>
          </div>
          
          <div class="cyber-card text-center p-6">
            <div class="w-24 h-24 bg-gradient-to-r from-cyber-green to-cyber-blue rounded-full mx-auto mb-4 flex items-center justify-center">
              <span class="text-cyber-dark font-orbitron font-bold text-xl">MJ</span>
            </div>
            <h3 class="text-lg font-orbitron font-bold mb-2 text-cyber-light">Mike Johnson</h3>
            <p class="text-cyber-green text-sm mb-3">Head of Design</p>
            <p class="text-cyber-light/70 text-xs leading-relaxed">
              Creative director with a passion for user experience design. Former UI/UX lead at Google.
            </p>
          </div>
          
          <div class="cyber-card text-center p-6">
            <div class="w-24 h-24 bg-gradient-to-r from-cyber-blue to-cyber-green rounded-full mx-auto mb-4 flex items-center justify-center">
              <span class="text-cyber-dark font-orbitron font-bold text-xl">SW</span>
            </div>
            <h3 class="text-lg font-orbitron font-bold mb-2 text-cyber-light">Sarah Wilson</h3>
            <p class="text-cyber-blue text-sm mb-3">Community Manager</p>
            <p class="text-cyber-light/70 text-xs leading-relaxed">
              Dedicated to building and nurturing our global gaming community. Former esports organizer.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section class="contact-section">
      <div class="container mx-auto px-4 py-16">
        <div class="cyber-card p-8">
          <div class="text-center mb-12">
            <h2 class="text-4xl font-orbitron font-bold mb-6 text-cyber-light">Get In Touch</h2>
            <p class="text-xl text-cyber-light/70 max-w-3xl mx-auto">
              Have questions, suggestions, or just want to say hello? We'd love to hear from you!
            </p>
          </div>
          
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Contact Information</h3>
              <div class="space-y-6">
                <div class="flex items-center space-x-4">
                  <div class="w-12 h-12 bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-cyber-light font-semibold">Email</h4>
                    <p class="text-cyber-light/70">zhangshuaidoc@163.com</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Follow Us</h3>
              <p class="text-cyber-light/70 mb-6">
                Stay updated with the latest news, game releases, and community events.
              </p>
              <div class="flex space-x-4">
                <a href="#" class="cyber-social-button">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" class="cyber-social-button">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </a>
                <a href="#" class="cyber-social-button">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
                  </svg>
                </a>
                <a href="#" class="cyber-social-button">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
// 这个页面主要是静态内容，不需要太多交互逻辑
</script>

<style scoped>
.about-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.hero-section {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%);
  backdrop-filter: blur(10px);
}

.cyber-button-large {
  @apply bg-gradient-to-r from-cyber-blue/20 to-cyber-purple/20 border border-cyber-blue/30 rounded-lg p-6 text-center hover:from-cyber-blue/30 hover:to-cyber-purple/30 transition-all duration-300;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.cyber-card-large {
  @apply bg-cyber-gray/50 border border-cyber-gray/30 rounded-lg backdrop-blur-sm;
  position: relative;
  overflow: hidden;
}

.cyber-card-large::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1));
  z-index: -1;
}

.cyber-social-button {
  @apply w-12 h-12 bg-gradient-to-r from-cyber-blue/20 to-cyber-purple/20 border border-cyber-blue/30 rounded-lg flex items-center justify-center text-cyber-light hover:from-cyber-blue/30 hover:to-cyber-purple/30 hover:scale-110 transition-all duration-300;
}

.story-section {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.5) 0%, rgba(22, 33, 62, 0.5) 100%);
}

.mission-section {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
}

.tech-section {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.5) 0%, rgba(22, 33, 62, 0.5) 100%);
}

.team-section {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
}

.contact-section {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.5) 0%, rgba(22, 33, 62, 0.5) 100%);
}

/* 响应式设计 */
@media (max-width: 640px) {
  .hero-section h1 {
    font-size: 3rem;
  }
  
  .cyber-button-large {
    padding: 1rem;
  }
}
</style> 